import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import useSWR from "swr";
import Breadcrumb from "../../../../Components/Common/Breadcrumb";
import Tabs from "../../../../Components/Common/Tabs";
import { staticPagesTabsData } from "../../../../data/SidebarData";
import Input from "../../../../Components/Common/Input";
import HtmlEditor from "../../../../Components/Common/HtmlEditor";
import ImageSelector from "../../../../Components/Common/ImageSelector";
import Button from "../../../../Components/Common/Button";
import Modal from "../../../../Components/Common/Modal";
import Storage from "../../Storage";
import Selector from "../../../../Components/Common/Selector";
import {
  sendPostRequestAuth,
  sendPutRequestAuth,
  fetcher,
} from "../../../../Api/swrConfig";
import { StaticPageApiUrls } from "../../../../Api/Auth/ApiUrls";
import useToastError from "../../../../hooks/useErrorToast";

const INITIAL_FORM_STATE = {
  title: "",
  slug: "",
  backgroundImage: "",
  subtitle: "",
  description: "",
  image: "",
  content: "",
};

const generateSlug = (value = "") =>
  value
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^\w-]+/g, "");

const slugToTitle = (slug = "") =>
  slug
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const parseContent = (content) => {
  if (!content) return {};
  if (typeof content === "object") return content;
  try {
    return JSON.parse(content);
  } catch (error) {
    console.warn("Failed to parse static page content", error);
    return {};
  }
};

const normalizeToSingle = (value) => {
  if (Array.isArray(value)) {
    return value[0] || "";
  }
  return value || "";
};

const STATIC_PAGES_QUERY = "/static?isStaticPage=true";

const StaticSinglePage = () => {
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [activeSlug, setActiveSlug] = useState("");
  const [initialSlug, setInitialSlug] = useState("");
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [statusMessage, setStatusMessage] = useState("");
  const [isModalOpen, setModalOpen] = useState(false);
  const [imageField, setImageField] = useState(null);
  const [selectedStaticSlug, setSelectedStaticSlug] = useState("");

  const { showErrorToast } = useToastError();

  const { data: staticPagesList, isValidating: isStaticListLoading } = useSWR(
    STATIC_PAGES_QUERY,
    fetcher
  );

  const staticPageOptions = useMemo(() => {
    if (!Array.isArray(staticPagesList?.data)) return [];
    return staticPagesList.data
      .filter((item) => item?.slug)
      .map((item) => ({
        label: item?.title || slugToTitle(item?.slug),
        value: item?.slug,
      }));
  }, [staticPagesList]);

  const fetchStaticPage = useCallback(
    async (url) => {
      try {
        return await fetcher(url);
      } catch (error) {
        if (error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    []
  );

  const { data, isValidating } = useSWR(
    activeSlug ? StaticPageApiUrls.info(activeSlug) : null,
    fetchStaticPage,
    {
      revalidateOnFocus: false,
    }
  );

  const resetForm = useCallback(
    (overrides = {}) => {
      setFormData({ ...INITIAL_FORM_STATE, ...overrides });
      setInitialSlug(overrides.slug || "");
      setSlugManuallyEdited(false);
      setErrors({});
      setSelectedStaticSlug(overrides.slug || "");
    },
    []
  );

  useEffect(() => {
    if (typeof data === "undefined") return;

    if (data === null) {
      if (activeSlug) {
        resetForm({
          slug: activeSlug,
          title: slugToTitle(activeSlug),
        });
        setStatusMessage(
          "No existing page found. Fill in the details to create a new page."
        );
      }
      return;
    }

    const pageData = data?.data;
    if (!pageData) return;

    const content = parseContent(pageData.content);

    setFormData({
      title: pageData.title || "",
      slug: pageData.slug || activeSlug,
      backgroundImage: content?.backgroundImage || "",
      subtitle: pageData.headerDescription || "",
      description: pageData.bodyDescription || "",
      image: pageData.image || "",
      content: content?.content || content?.contentHtml || "",
    });
    setSelectedStaticSlug(pageData.slug || "");

    setInitialSlug(pageData.slug || "");
    setSlugManuallyEdited(false);
    setErrors({});
    setStatusMessage("Loaded existing page data.");
  }, [data, activeSlug, resetForm]);

  const isExistingPage = useMemo(() => Boolean(initialSlug), [initialSlug]);

  const handleInputChange = (name, value) => {
    if (name === "slug") {
      const sanitized = generateSlug(value);
      setSlugManuallyEdited(true);
      setSelectedStaticSlug("");
      setFormData((prev) => ({ ...prev, slug: sanitized }));
      return;
    }

    setFormData((prev) => {
      const updated = { ...prev, [name]: value };

      if (name === "title" && !slugManuallyEdited) {
        updated.slug = generateSlug(value);
        setSelectedStaticSlug("");
      }

      return updated;
    });
  };

  const handleImageSelectorChange = (key, value) => {
    const normalizedValue = normalizeToSingle(value);

    if (!key) return;

    setFormData((prev) => ({ ...prev, [key]: normalizedValue }));
  };

  const handleSelectExistingPage = (_, option) => {
    const selectedSlug = option?.value;
    if (selectedSlug) {
      setSelectedStaticSlug(selectedSlug);
      setActiveSlug(selectedSlug);
      setStatusMessage("Fetching page data...");
    } else {
      setSelectedStaticSlug("");
      setActiveSlug("");
      resetForm();
      setStatusMessage("");
    }
  };

  const handleModalConfirm = (files) => {
    if (Array.isArray(files) && files.length && imageField) {
      handleImageSelectorChange(imageField, files[0]?.fileUrl);
    }
    setModalOpen(false);
    setImageField(null);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title?.trim()) {
      newErrors.title = "Title is required.";
    }

    if (!formData.slug?.trim()) {
      newErrors.slug = "Slug is required.";
    }

    if (!formData.subtitle?.trim()) {
      newErrors.subtitle = "Subtitle is required.";
    }

    if (!formData.description?.trim()) {
      newErrors.description = "Description is required.";
    }

    // if (!formData.content?.trim()) {
    //   newErrors.content = "Content is required.";
    // }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  const buildPayload = () => {
    return {
      title: formData.title?.trim(),
      slug: formData.slug?.trim(),
      displayTitle: formData.title?.trim(),
      headerDescription: formData.subtitle,
      bodyDescription: formData.description,
      image: formData.image,
      visibility: true,
      content: JSON.stringify({
        backgroundImage: formData.backgroundImage,
        content: formData.content,
        isStaticPage: true,
      }),
    };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateForm()) {
      toast.error("Please resolve the highlighted issues before saving.");
      return;
    }

    const payload = buildPayload();
    const isCreating = !isExistingPage;

    if (!payload.slug) {
      setErrors((prev) => ({ ...prev, slug: "Slug is required." }));
      toast.error("Slug is required to save the page.");
      return;
    }

    try {
      setIsSaving(true);
      if (isCreating) {
        await sendPostRequestAuth(StaticPageApiUrls.create, { arg: payload });
        toast.success("Static page created successfully.");
        setInitialSlug(payload.slug);
        setActiveSlug(payload.slug);
        setStatusMessage("Page created successfully.");
      } else {
        const updatePayload = { ...payload };
        delete updatePayload.slug;
        await sendPutRequestAuth(
          StaticPageApiUrls.edit(initialSlug || payload.slug),
          { arg: updatePayload }
        );
        toast.success("Static page updated successfully.");
        setInitialSlug(payload.slug);
        setActiveSlug(payload.slug);
        setStatusMessage("Page updated successfully.");
      }
    } catch (error) {
      if (
        !isExistingPage &&
        error.response?.data?.message === "Static Page not found."
      ) {
        try {
          await sendPostRequestAuth(StaticPageApiUrls.create, { arg: payload });
          toast.success("Static page created successfully.");
          setInitialSlug(payload.slug);
          setActiveSlug(payload.slug);
          setStatusMessage("Page created successfully.");
        } catch (creationError) {
          showErrorToast(creationError);
        }
      } else {
        showErrorToast(error);
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearForm = () => {
    resetForm();
    setActiveSlug("");
    setStatusMessage("");
    setSelectedStaticSlug("");
  };

  return (
    <div>
      <Breadcrumb actionButtons={[]} />
      <Tabs tabs={staticPagesTabsData} />
      <div className="bg-white shadow-sm rounded-lg mt-4 p-4 flex flex-col gap-6">
        <div className="flex flex-col md:flex-row md:items-end gap-3">
          <div className="flex-1">
            <Selector
              name="staticPage"
              label="Existing static page"
              placeholder="Select a page to edit"
              value={selectedStaticSlug}
              options={staticPageOptions}
              onChange={handleSelectExistingPage}
              isSearchable
              isClearable
              disabled={isStaticListLoading}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="secondary"
              size="small"
              onClick={handleClearForm}
              disabled={isValidating}
            >
              New page
            </Button>
          </div>
        </div>

        {statusMessage && (
          <div className="text-sm text-gray-600 bg-gray-100 border border-gray-200 rounded-md px-3 py-2">
            {statusMessage}
          </div>
        )}

        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
          noValidate
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Input
              label="Page title"
              name="title"
              placeholder="Enter page title"
              value={formData.title}
              onChange={handleInputChange}
              error={errors.title}
              required
            />
            <Input
              label="Page slug"
              name="slug"
              placeholder="unique-page-slug"
              value={formData.slug}
              onChange={handleInputChange}
              error={errors.slug}
              required
              disabled={isExistingPage}
            />
          </div>

          <Input
            label="Subtitle"
            name="subtitle"
            placeholder="Short subtitle for the page"
            value={formData.subtitle}
            onChange={handleInputChange}
            error={errors.subtitle}
            required
          />

          <Input
            label="Description"
            name="description"
            placeholder="Add a brief description"
            value={formData.description}
            onChange={handleInputChange}
            error={errors.description}
            required
          />

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <ImageSelector
              label="Background image"
              name="backgroundImage"
              value={formData.backgroundImage}
              setModalOpen={setModalOpen}
              onClick={() => setImageField("backgroundImage")}
              onChange={handleImageSelectorChange}
            />
            <ImageSelector
              label="Featured image"
              name="image"
              value={formData.image}
              setModalOpen={setModalOpen}
              onClick={() => setImageField("image")}
              onChange={handleImageSelectorChange}
            />
          </div>

          <HtmlEditor
            label="Content"
            name="content"
            placeholder="Compose the page body"
            value={formData.content}
            onChange={handleInputChange}
            error={errors.content}
          />

          <div className="flex justify-end">
            <Button
              type="primary"
              size="small"
              onClick={handleSubmit}
              isLoading={isSaving}
            >
              {isSaving ? "Saving..." : "Confirm"}
            </Button>
          </div>
        </form>
      </div>

      <Modal
        isOpen={isModalOpen}
        title="Storage Manager"
        onCancel={() => {
          setModalOpen(false);
          setImageField(null);
        }}
        footer={false}
      >
        <Storage isSelector onConfirm={handleModalConfirm} />
      </Modal>
    </div>
  );
};

export default StaticSinglePage;
